import { useAuth } from 'react-oidc-context';
import {useEffect, useState} from 'react';
import { Storage } from '../../Utils/StorageUtils';
import { useAppDispatch } from '../Hooks';
import {
    login,
    logout,
    synchronizeUnauthenticatedState,
} from '../Reducers/AuthenticationReducer';
import axios from 'axios';

export const useAuthToken = () => {
    const auth = useAuth();
    const dispatch = useAppDispatch();
    const AUTH_TOKEN_KEY = 'api-token-key';
    const accessToken = auth?.user?.access_token;
    const [synchronizedToken, setSynchronizedToken] = useState<string | null>(() => {
        if (!auth?.isAuthenticated || !accessToken) {
            return null;
        }
        return Storage.local.get(AUTH_TOKEN_KEY) === accessToken ? accessToken : null;
    });

    useEffect(() => {
        if (auth.isLoading) return;
        console.log('useAuthToken - Auth State:', {
            isAuthenticated: auth?.isAuthenticated,
            hasAccessToken: !!auth?.user?.access_token,
            isLoading: auth?.isLoading,
            error: auth?.error,
            state: auth?.user?.state
        });

        if (auth?.isAuthenticated && accessToken) {
            console.log('useAuthToken - Storing token and dispatching login');
            
            // Store token and state in localStorage for persistence
            Storage.local.set(AUTH_TOKEN_KEY, accessToken);
            if (auth.user?.state) {
                Storage.local.set('auth_state', auth.user.state);
            }
            
            // Set axios headers
            const token = accessToken;
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            
            if (auth.user?.profile?.email) {
                Storage.local.set('user-mail', auth.user.profile.email);
                axios.defaults.headers.common['Email'] = auth.user.profile.email;
            }
            
            // Dispatch login action
            dispatch(login(auth));
            setSynchronizedToken(accessToken);
        } else if (auth?.error) {
            console.error('useAuthToken - Authentication error:', auth.error);
            // Clear stored state on error
            Storage.local.remove('auth_state');
            setSynchronizedToken(null);
        } else if(!auth?.isAuthenticated) {
            console.log('useAuthToken - User is not authenticated, clearing stored token and state.');
            Storage.local.remove(AUTH_TOKEN_KEY);
            Storage.local.remove('auth_state');
            setSynchronizedToken(null);
            dispatch(synchronizeUnauthenticatedState());
        }
    }, [accessToken, auth?.error, auth?.isAuthenticated, auth?.isLoading, dispatch]);

    return {
        token: accessToken,
        isAuthenticated:
            !auth?.isLoading &&
            !!auth?.isAuthenticated &&
            !!accessToken &&
            synchronizedToken === accessToken,
        user: auth?.user,
        signOut: () => {
            Storage.local.remove('auth_state');
            dispatch(logout());
            auth?.signoutRedirect();
        }
    };
};
